import React from "react";
import styled, { css } from "styled-components";
import { ArrowLeft01Icon, ArrowRight01Icon } from "@hugeicons/core-free-icons";
import HugeIcon from "src/components/HugeIcon";
import { colors } from "src/theme/colors";
import { line, radii, text } from "src/theme/ui";

/**
 * Pagination — football's control for a paginated table or list, ported.
 *
 * Left: the rows-per-page selector and the row window ("1–10 of 600"). Right:
 * numbered pages with ellipses, and prev/next.
 *
 * The active page is flat — a solid fill, no gradient and no inset highlight.
 * That design allows one lit surface per control, and this is it.
 */

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const;
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_OPTIONS[0];

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
`;

const Group = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Pages = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
`;

const buttonBase = css`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  min-width: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 9px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;

  &:disabled {
    pointer-events: none;
    opacity: 0.4;
  }
`;

/* Quiet: a ring rather than a border, so a page button takes no layout width
   from its outline and a row of them stays evenly spaced. */
const quiet = css`
  background: ${colors.white};
  box-shadow: inset 0 0 0 1px ${line.field};
  color: ${colors.gray[700]};

  &:hover {
    background: ${colors.gray[50]};
    color: ${colors.gray[800]};
  }
`;

const Step = styled.button`
  ${buttonBase}
  ${quiet}
`;

const Page = styled.button<{ $current: boolean }>`
  ${buttonBase}
  ${text.numeric}

  ${({ $current }) =>
    $current
      ? css`
          background: ${colors.primary[500]};
          color: ${colors.white};
          box-shadow: none;

          &:hover {
            background: ${colors.primary[500]};
            color: ${colors.white};
          }
        `
      : quiet}
`;

const Ellipsis = styled.span`
  padding: 0 4px;
  font-size: 12.5px;
  color: ${colors.gray[500]};
`;

const SizeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12.5px;
  color: ${colors.gray[600]};
`;

const SizeSelect = styled.select`
  ${text.numeric}
  height: 32px;
  padding: 0 8px;
  border: 0;
  border-radius: 9px;
  background: ${colors.white};
  box-shadow: inset 0 0 0 1px ${line.field};
  color: ${colors.gray[800]};
  font-family: inherit;
  font-size: 12px;
  cursor: pointer;
  outline: none;

  &:focus-visible {
    box-shadow: inset 0 0 0 1px ${colors.primary[200]},
      0 0 0 4px ${colors.primary[15]};
  }
`;

const Window = styled.span`
  ${text.numeric}
  font-size: 12px;
  color: ${colors.gray[600]};
`;

export interface PaginationProps {
  page: number;
  totalPages: number;
  total: number;
  pageSize: number;
  onPage: (page: number) => void;
  /** When given, renders the rows-per-page selector. */
  onPageSize?: (size: number) => void;
  className?: string;
}

const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  total,
  pageSize,
  onPage,
  onPageSize,
  className,
}) => {
  /* Nothing to page through and nothing to resize: no bar at all, rather than
     a bar that says "1–3 of 3" beside two dead arrows. */
  if (total <= 0 || (total <= DEFAULT_PAGE_SIZE && page === 1)) return null;

  const from = (page - 1) * pageSize + 1;
  const to = Math.min(page * pageSize, total);

  /* The compact window: 1 … p-1 p p+1 … N, collapsing runs into one gap. */
  const pages: (number | "…")[] = [];
  const push = (v: number | "…") => {
    if (pages[pages.length - 1] !== v) pages.push(v);
  };
  for (let i = 1; i <= totalPages; i++) {
    if (i === 1 || i === totalPages || Math.abs(i - page) <= 1) push(i);
    else push("…");
  }

  return (
    <Row className={className}>
      <Group>
        {onPageSize && (
          <SizeLabel>
            Rows per page
            <SizeSelect
              value={pageSize}
              onChange={(e) => onPageSize(Number(e.target.value))}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </SizeSelect>
          </SizeLabel>
        )}
        <Window>
          {from.toLocaleString()}–{to.toLocaleString()} of{" "}
          {total.toLocaleString()}
        </Window>
      </Group>

      <Pages>
        <Step
          type="button"
          aria-label="Previous page"
          disabled={page <= 1}
          onClick={() => onPage(page - 1)}
        >
          <HugeIcon icon={ArrowLeft01Icon} size={14} />
        </Step>

        {pages.map((p, i) =>
          p === "…" ? (
            <Ellipsis key={`gap-${i}`} aria-hidden="true">
              …
            </Ellipsis>
          ) : (
            <Page
              key={p}
              type="button"
              $current={p === page}
              aria-label={`Go to page ${p}`}
              aria-current={p === page ? "page" : undefined}
              onClick={() => onPage(p)}
            >
              {p}
            </Page>
          ),
        )}

        <Step
          type="button"
          aria-label="Next page"
          disabled={page >= totalPages}
          onClick={() => onPage(page + 1)}
        >
          <HugeIcon icon={ArrowRight01Icon} size={14} />
        </Step>
      </Pages>
    </Row>
  );
};

export default Pagination;
